import { isEmpty } from 'lodash';
import Multiselect from 'multiselect-react-dropdown';
import React, { Suspense, useEffect, useState } from 'react';
import { Button, Form, HelpBlock, Modal, OverlayTrigger, Panel, Row, Tooltip } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { navigate } from 'raviger';
import PageHeader from '../../components/PageHeader/PageHeader';
import Permission from '../../components/Permission/Permission';
import SideMenu from '../../components/SideMenu/SideMenu';
import Spinner from '../../components/Spinner/Spinner';
import * as constants from '../../constants';
import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import { breadcrumbAddRoute } from '../../store/actions/breadcrumb';
import { useFranchiseStore } from '../../store/providers/Franchise';
import { useTransactionDocumentStore } from '../../store/providers/TransactionDocument';
import './smartDocsMapping.css';
import { useSmartDocCopyStore } from '../../store/providers/SmartDoc';
import withForm from '../../hoc/withForm';
import SmartDocMappingRow from '../../components/SmartDocMappings/SmartDocMappingRow';

const FormInput = React.lazy(() => import('../../components/FormInput/FormInput'));
const InputCollection = React.lazy(() => import('../../components/InputCollection/InputCollection'));

const ProcessingModal = ({ show, onClose }) => (
    <Modal show={show} onHide={onClose} backdrop="static">
        <Modal.Body>
            <p style={{ fontSize: '16px' }}>Your SmartDocs Mapping request has been submitted and is now processing in the background.</p>
            <p style={{ fontSize: '16px' }}>You may continue using Franchise Manager while the process completes.</p>
            <p style={{ fontSize: '16px' }}>When the job finishes, a summary email will be sent with the results, including any documents that could not be processed.</p>
        </Modal.Body>
        <Modal.Footer>
            <Button bsStyle="danger" variant="danger" onClick={onClose}>Close</Button>
        </Modal.Footer>
    </Modal>
);

const SkeletonBar = () => <div className="smart-docs-mapping-skeleton" />;

function SmartDocsMapping() {
    const dispatch = useDispatch();

    const [isMounted, setIsMounted] = useState(false);
    const [franchiseOptions, setFranchiseOptions] = useState([]);

    const [sourceInstanceId, setSourceInstanceId] = useState(null);
    const [destinationInstanceId, setDestinationInstanceId] = useState(null);
    const [pendingDocFetch, setPendingDocFetch] = useState(null);
    const [rows, setRows] = useState([]);
    const [destinationDocumentOptions, setDestinationDocumentOptions] = useState([]);
    const [isBusy, setIsBusy] = useState(false);
    const [showProcessingModal, setShowProcessingModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSourceDocsLoading, setIsSourceDocsLoading] = useState(false);
    const [isDestinationDocsLoading, setIsDestinationDocsLoading] = useState(false);
    const { listRecords, data: franchiseData } = useFranchiseStore();
    const { data: documentsData, filterRecords: getDocuments } = useTransactionDocumentStore();
    const { copyMappingsBetweenFranchise } = useSmartDocCopyStore();
    const pagePermission = constants.PERMISSION_CONTENT_FAQQUESTIONS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const showGrid = isSourceDocsLoading || rows.length > 0;
    const hasDocumentSelected = rows.some((row) => row.copyPdf || (row.copyPdf && row.copyMapping) || (row.destinationDocumentId && row.copyMapping));
    const skeletonRows = Array.from({ length: 4 });
    const isSaveEnabled = canEdit && !isBusy && sourceInstanceId && destinationInstanceId && hasDocumentSelected;
    const selectedDocumentsCount = rows.filter((row) => row.copyPdf || row.copyMapping)?.length;

    const [multiSelectKey, setMultiSelectKey] = useState(0);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        listRecords();
        dispatch(breadcrumbAddRoute({ key: 'smartdocsMapping', parent: 'Content', text: 'SmartDocs Mapping' }));
    }, [isMounted, hasPermission]);

    useEffect(() => {
        if (!isEmpty(franchiseData)
            && franchiseData.scope === 'list'
            && franchiseData.status === 'success'
        ) {
            const options = franchiseData.body.map((franchise) => {
                return { id: franchise.id, name: franchise.instance };
            });
            setFranchiseOptions(options);
            setIsMounted(true);
        }
    }, [franchiseData]);

    useEffect(() => {
        if (!isEmpty(documentsData)
            && documentsData.scope === 'list'
            && documentsData.status === 'success'
        ) {
            const options = documentsData.body.map((document) => {
                return { id: document.id, name: document.name };
            });

            if (pendingDocFetch === 'source') {
                setRows(options.map((doc) => ({
                    sourceDocumentId: doc.id,
                    sourceDocumentName: doc.name,
                    destinationDocumentId: '',
                    copyPdf: false,
                    copyMapping: false,
                })));
                setIsSourceDocsLoading(false);
            } else if (pendingDocFetch === 'destination') {
                setDestinationDocumentOptions(options);
                setIsDestinationDocsLoading(false);
            }
            setPendingDocFetch(null);
        }
    }, [documentsData]);

    const fetchActiveDocuments = (franchiseId, which) => {
        const isSource = which === 'source';
        if (isSource) {
            setIsSourceDocsLoading(true);
        } else {
            setIsDestinationDocsLoading(true);
        }

        setPendingDocFetch(which);
        getDocuments({
            franchiseId,
            transactionTypeId: null,
            status: constants.DOCUMENT_STATUS_ENUM.Active,
            mappingCompletedOnly: isSource
        });
    };

    const onChangeSourceInstance = (item) => {
        setMultiSelectKey(prev => prev + 1);
        const franchiseId = item ? item.id : null;
        setSourceInstanceId(franchiseId);
        if (franchiseId === destinationInstanceId) {
            onChangeDestinationInstance(null);
        }

        setErrors((prev) => ({ ...prev, sourceInstanceId: null, sameInstance: null }));
        setRows([]);

        if (franchiseId) {
            fetchActiveDocuments(franchiseId, 'source');
        } else {
            setIsSourceDocsLoading(false);
        }
    };

    const onChangeDestinationInstance = (item) => {
        setMultiSelectKey(prev => prev + 1);
        const franchiseId = item ? item.id : null;
        setDestinationInstanceId(franchiseId);
        setErrors((prev) => ({ ...prev, destinationInstanceId: null, sameInstance: null }));

        setRows((prev) => prev.map((row) => ({ ...row, destinationDocumentId: '' })));

        if (franchiseId) {
            fetchActiveDocuments(franchiseId, 'destination');
        } else {
            setDestinationDocumentOptions([]);
            setIsDestinationDocsLoading(false);
        }
    };

    const updateRow = React.useCallback((index, field, value) => {
        setRows((prev) => {
            const next = [...prev];
            let selectedRow = next[index];
            selectedRow = { ...selectedRow, [field]: value };
            if (!selectedRow.destinationDocumentId && !selectedRow.copyPdf) {
                selectedRow.copyMapping = false;
            }
            next[index] = selectedRow;
            return next;
        });
    }, []);

    const selectAll = (field) => {
        setRows((prev) => {
            if (field === "copyPdf") {
                const allSelected = prev.every((row) => row.copyPdf);

                return prev.map((row) => {
                    const updatedRow = {
                        ...row,
                        copyPdf: !allSelected,
                    };

                    if (!updatedRow.destinationDocumentId && !updatedRow.copyPdf) {
                        updatedRow.copyMapping = false;
                    }

                    return updatedRow;
                });
            } else {
                const enabledRows = prev.filter(
                    row => row.destinationDocumentId || row.copyPdf
                );

                const allEnabledSelected =
                    enabledRows.length > 0 &&
                    enabledRows.every(row => row[field]);

                return prev.map(row => {
                    const isEnabled = row.destinationDocumentId || row.copyPdf;

                    return isEnabled
                        ? { ...row, [field]: !allEnabledSelected }
                        : row;
                });
            }
        });
    };

    const validate = () => {
        const nextErrors = {};

        if (!sourceInstanceId) {
            nextErrors.sourceInstanceId = 'Source Instance is required.';
        }
        if (!destinationInstanceId) {
            nextErrors.destinationInstanceId = 'Destination Instance is required.';
        }
        if (sourceInstanceId && destinationInstanceId && sourceInstanceId === destinationInstanceId) {
            nextErrors.sameInstance = 'Source and Destination Instance cannot be the same.';
        }

        const hasAtLeastOneAction = rows.some((row) => row.copyPdf || row.copyMapping);
        if (rows.length > 0 && !hasAtLeastOneAction) {
            nextErrors.rows = 'Select Copy PDF and/or Copy Mapping for at least one document before saving.';
        }

        setErrors(nextErrors);
        return isEmpty(nextErrors);
    };

    const submittedHandler = async (e) => {
        e.preventDefault();

        if (!validate()) { return; }

        setIsBusy(true);
        setShowProcessingModal(true);
        const documents = rows
            .filter((row) => row.copyPdf || (row.destinationDocumentId && row.copyMapping))
            .map((row) => ({
                sourceDocumentId: row.sourceDocumentId,
                destinationDocumentId: row.destinationDocumentId || null,
                copyPdf: row.copyPdf,
                copyMapping: row.copyMapping,
            }));

        try {
            const payload = {
                sourceInstanceId,
                destinationInstanceId,
                documents,
            };

            const response = await copyMappingsBetweenFranchise(payload);

            if (response?.success) {
                setSourceInstanceId(null);
                setDestinationInstanceId(null);
                setRows([]);
                setDestinationDocumentOptions([]);
            }
        } finally {
            setPendingDocFetch(null);
            setIsSourceDocsLoading(false);
            setIsDestinationDocsLoading(false);
            setIsBusy(false);
        }
    };

    return (
        <>
            <SideMenu activeKey="content" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {
                            !isMounted
                                ? <Spinner text="Loading..." />
                                : <div style={{ paddingRight: '15px' }}>
                                    <PageHeader title="SmartDocs Mapping" />
                                    <Panel>
                                        <Panel.Body>
                                            <Form onSubmit={submittedHandler}>
                                                <h4>Instance Selection</h4>
                                                <hr className="smart-docs-mapping-select-all" />
                                                {errors.sameInstance &&
                                                    <div style={{ marginTop: 10 }} >
                                                        <HelpBlock className="input-collection-helpblock-inline" >
                                                            <i className="fa fa-exclamation-circle text-danger"></i>&nbsp;
                                                            <div className="text-danger smart-docs-mapping-error">{errors.sameInstance}</div>
                                                        </HelpBlock>
                                                    </div>
                                                }
                                                <Row>
                                                    <div className="col-md-4">
                                                        <label className="smart-docs-mapping-label">
                                                            Source Instance <span className="text-danger">*</span>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id="source-instance-tooltip">
                                                                        Choose the instance you want to copy SmartDocs from.
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i
                                                                    style={{ marginBottom: '2px' }}
                                                                    className="fa fa-question-circle form-label-tip-margin"
                                                                />
                                                            </OverlayTrigger>
                                                        </label>
                                                        <InputCollection helpBlockClassName="input-collection-helpblock-inline" required errorMessage={errors.sourceInstanceId}>
                                                            <Multiselect
                                                                key={`source-select-${multiSelectKey}`}
                                                                showArrow={true}
                                                                selectedValues={sourceInstanceId ? [franchiseOptions.find(f => f.id === sourceInstanceId)] : []}
                                                                options={franchiseOptions}
                                                                selectionLimit={1}
                                                                displayValue="name"
                                                                disable={isBusy || isSourceDocsLoading || isDestinationDocsLoading}
                                                                placeholder="Select Instance..."
                                                                emptyRecordMsg="No Instances Available"
                                                                className="smart-docs-mapping"
                                                                closeOnSelect={true}
                                                                onSelect={(list, item) => onChangeSourceInstance(item)}
                                                                onRemove={() => onChangeSourceInstance(null)} />
                                                        </InputCollection>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="smart-docs-mapping-label">
                                                            Destination Instance <span className="text-danger">*</span>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id="destination-instance-tooltip">
                                                                        Choose the instance you want to copy SmartDocs to.
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i
                                                                    style={{ marginBottom: '2px' }}
                                                                    className="fa fa-question-circle form-label-tip-margin"
                                                                />
                                                            </OverlayTrigger>
                                                        </label>
                                                        <InputCollection helpBlockClassName="input-collection-helpblock-inline" required errorMessage={errors.destinationInstanceId}>
                                                            <Multiselect
                                                                key={`destination-select-${multiSelectKey}`}
                                                                showArrow={true}
                                                                selectedValues={destinationInstanceId ? [franchiseOptions.find(f => f.id === destinationInstanceId)] : []}
                                                                options={franchiseOptions.filter(f => f.id !== sourceInstanceId)}
                                                                selectionLimit={1}
                                                                displayValue="name"
                                                                disable={isBusy || isSourceDocsLoading || isDestinationDocsLoading}
                                                                placeholder="Select Instance..."
                                                                emptyRecordMsg="No Instances Available"
                                                                className="smart-docs-mapping"
                                                                closeOnSelect={true}
                                                                onSelect={(list, item) => onChangeDestinationInstance(item)}
                                                                onRemove={() => onChangeDestinationInstance(null)} />
                                                        </InputCollection>
                                                    </div>
                                                </Row>

                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <h4 className="smart-docs-mapping-section-title" style={{ margin: 0 }}>
                                                        Documents
                                                    </h4>

                                                    {selectedDocumentsCount > 0 && (
                                                        <span
                                                            style={{
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                color: '#555',
                                                            }}
                                                        >
                                                            Copying {selectedDocumentsCount}{' '}
                                                            {selectedDocumentsCount === 1 ? 'document' : 'documents'}
                                                        </span>
                                                    )}
                                                </div>
                                                <hr className="smart-docs-mapping-select-all" />

                                                {showGrid
                                                    ? <>
                                                        <p className="smart-docs-mapping-help-text ">Helpful descriptive text about this section goes here.</p>
                                                        <Row className="smart-docs-mapping-grid-header">
                                                            <div className="col-md-4" >
                                                                Source Document

                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id="source-document-tooltip">
                                                                            The active SmartDoc in the Source Instance.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i
                                                                        style={{ marginBottom: '2px' }}
                                                                        className="fa fa-question-circle form-label-tip-margin"
                                                                    />
                                                                </OverlayTrigger>
                                                            </div>

                                                            <div className="col-md-4" >
                                                                Destination Document

                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id="destination-document-tooltip">
                                                                            Optional. Leave blank to create a new document in the Destination Instance.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i
                                                                        style={{ marginBottom: '2px' }}
                                                                        className="fa fa-question-circle form-label-tip-margin"
                                                                    />
                                                                </OverlayTrigger>
                                                            </div>
                                                            <div className="col-md-2 text-center" style={{ paddingRight: 25, paddingLeft: 0 }}>
                                                                Copy PDF

                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id="copy-pdf-tooltip">
                                                                            Copy PDF copies the source document to the selected destination instance. If you don't select a destination document, a new document will automatically be created in the destination instance.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i
                                                                        style={{ marginBottom: '2px' }}
                                                                        className="fa fa-question-circle form-label-tip-margin"
                                                                    />
                                                                </OverlayTrigger>

                                                                <br />

                                                                <a
                                                                    href="#"
                                                                    className="smart-docs-mapping-select-all "
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        selectAll('copyPdf');
                                                                    }}
                                                                >
                                                                    {rows.length > 0 && rows.every(r => r.copyPdf)
                                                                        ? "Deselect All"
                                                                        : "Select All"}
                                                                </a>
                                                            </div>
                                                            <div className="col-md-2 text-center" style={{ paddingRight: 25, paddingLeft: 0 }}>
                                                                Copy Mapping

                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id="copy-mapping-tooltip">
                                                                            Copy Mapping copies the mapping from the source document and applies it to the selected destination document.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i
                                                                        style={{ marginBottom: '2px' }}
                                                                        className="fa fa-question-circle form-label-tip-margin"
                                                                    />
                                                                </OverlayTrigger>
                                                                <br />
                                                                <a
                                                                    href="#"
                                                                    className="smart-docs-mapping-select-all "
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        selectAll('copyMapping');
                                                                    }}
                                                                >
                                                                    {rows.length > 0 && rows.every(r => r.copyMapping)
                                                                        ? "Deselect All"
                                                                        : "Select All"}
                                                                </a>
                                                            </div>
                                                        </Row>
                                                        <div
                                                            style={{
                                                                maxHeight: 'calc(100vh - 580px)',
                                                                overflowY: 'auto',
                                                                overflowX: 'hidden',
                                                                scrollbarGutter: 'stable',
                                                            }}
                                                        >
                                                            {isSourceDocsLoading
                                                                ? skeletonRows.map((_, index) => (
                                                                    <Row className="smart-docs-mapping-grid-row" key={`skeleton-row-${index}`}>
                                                                        <div className="col-md-4">
                                                                            <SkeletonBar />
                                                                        </div>
                                                                        <div className="col-md-4">
                                                                            <SkeletonBar />
                                                                        </div>
                                                                        <div className="col-md-2 smart-docs-mapping-checkbox-cell">
                                                                            <FormInput
                                                                                type="checkbox"
                                                                                id={`skeleton_${index}_copyPdf`}
                                                                                value={false}
                                                                                disabled={true}
                                                                                changed={() => { }}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-2 smart-docs-mapping-checkbox-cell">
                                                                            <FormInput
                                                                                type="checkbox"
                                                                                id={`skeleton_${index}_copyMapping`}
                                                                                value={false}
                                                                                disabled={true}
                                                                                changed={() => { }}
                                                                            />
                                                                        </div>
                                                                    </Row>
                                                                ))
                                                                : rows.map((row, index) => (
                                                                    <SmartDocMappingRow
                                                                        key={row.sourceDocumentId}
                                                                        row={row}
                                                                        index={index}
                                                                        destinationDocumentOptions={destinationDocumentOptions}
                                                                        isDestinationDocsLoading={isDestinationDocsLoading}
                                                                        isBusy={isBusy}
                                                                        destinationInstanceId={destinationInstanceId}
                                                                        onUpdate={updateRow}
                                                                    />
                                                                ))
                                                            }
                                                        </div>
                                                    </>
                                                    : sourceInstanceId && !rows.length
                                                        ? <p className="smart-docs-mapping-help-text text-center pt-20">No documents are available for the selected <strong>Source Instance</strong>.</p>
                                                        : <p className="smart-docs-mapping-help-text text-center pt-20">Please select a <strong>Source Instance</strong> to view available documents.</p>
                                                }

                                                <div
                                                    style={{
                                                        position: 'sticky',
                                                        bottom: 0,
                                                        background: '#fff',
                                                        padding: '15px 0',
                                                        borderTop: rows.length > 0 ? '1px solid #ddd' : 'unset',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        zIndex: 10,
                                                    }}
                                                >
                                                    <div>
                                                        {errors.rows && (
                                                            <HelpBlock
                                                                className="input-collection-helpblock-inline"
                                                                style={{
                                                                    margin: 0,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <i className="fa fa-exclamation-circle text-danger"></i>
                                                                <div
                                                                    className="text-danger smart-docs-mapping-error"
                                                                    style={{ marginLeft: 5 }}
                                                                >
                                                                    {errors.rows}
                                                                </div>
                                                            </HelpBlock>
                                                        )}
                                                    </div>

                                                    <Button
                                                        bsStyle="danger"
                                                        variant="danger"
                                                        type="submit"
                                                        disabled={!isSaveEnabled}
                                                    >
                                                        <i className="fa fa-save button-icon"></i>
                                                        {isBusy ? 'Processing...' : 'Save'}
                                                    </Button>
                                                </div>
                                            </Form>
                                        </Panel.Body>
                                    </Panel>

                                    <ProcessingModal
                                        show={showProcessingModal}
                                        onClose={() => navigate("/dashboard")} />
                                </div>
                        }
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(withForm(SmartDocsMapping)));